/**
 * APK Storage Service
 * Handles direct APK file uploads to Firebase Storage
 * This is the primary/preferred method for APK distribution
 */

import { ref, uploadBytesResumable, getDownloadURL, deleteObject, listAll, getMetadata } from 'firebase/storage';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db, storage } from './firebaseService';

export interface APKUploadProgress {
    bytesTransferred: number;
    totalBytes: number;
    progress: number; // 0-100
    state: 'running' | 'paused' | 'success' | 'error';
}

export interface APKFileInfo {
    filename: string;
    version: string;
    size: number;
    sizeFormatted: string;
    downloadUrl: string;
    uploadedAt: string;
    uploadedBy: string;
}

const APK_CONFIG_DOC = 'config/apk_upload';
const APK_STORAGE_PATH = 'apk-files';

/**
 * Upload APK file to Firebase Storage with progress tracking
 */
export async function uploadAPKFile(
    file: File,
    version: string,
    uploadedBy: string,
    onProgress?: (progress: APKUploadProgress) => void
): Promise<APKFileInfo> {
    try {
        // Validate file
        if (!file.name.endsWith('.apk')) {
            throw new Error('File must be an APK file (.apk extension)');
        }

        if (file.size > 200 * 1024 * 1024) { // 200MB limit
            throw new Error('APK file must be less than 200MB');
        }

        // Create storage reference with timestamp to avoid conflicts
        const timestamp = Date.now();
        const filename = `luca-v${version}-${timestamp}.apk`;
        const storageRef = ref(storage, `${APK_STORAGE_PATH}/${filename}`);

        // Upload file with progress tracking
        const uploadTask = uploadBytesResumable(storageRef, file, {
            contentType: 'application/vnd.android.package-archive',
            customMetadata: {
                version: version,
                uploadedBy: uploadedBy,
                uploadedAt: new Date().toISOString()
            }
        });

        // Track upload progress
        return new Promise((resolve, reject) => {
            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    const progress: APKUploadProgress = {
                        bytesTransferred: snapshot.bytesTransferred,
                        totalBytes: snapshot.totalBytes,
                        progress: Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
                        state: snapshot.state as any
                    };

                    if (onProgress) {
                        onProgress(progress);
                    }

                    console.log(`📤 Upload progress: ${progress.progress}% (${formatBytes(progress.bytesTransferred)} / ${formatBytes(progress.totalBytes)})`);
                },
                (error) => {
                    console.error('❌ Upload failed:', error);
                    reject(error);
                },
                async () => {
                    try {
                        // Get download URL
                        const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

                        const apkInfo: APKFileInfo = {
                            filename,
                            version,
                            size: file.size,
                            sizeFormatted: formatBytes(file.size),
                            downloadUrl,
                            uploadedAt: new Date().toISOString(),
                            uploadedBy
                        };

                        // Save to Firestore for easy retrieval
                        await saveAPKConfig(apkInfo);

                        console.log('✅ Upload complete:', apkInfo);
                        resolve(apkInfo);
                    } catch (error) {
                        reject(error);
                    }
                }
            );
        });
    } catch (error) {
        console.error('❌ Upload error:', error);
        throw error;
    }
}

/**
 * Save APK configuration to Firestore
 */
async function saveAPKConfig(apkInfo: APKFileInfo): Promise<void> {
    try {
        // 1. Save to upload history/config
        const configRef = doc(db, APK_CONFIG_DOC);
        await setDoc(configRef, {
            latest: apkInfo,
            updatedAt: new Date().toISOString()
        }, { merge: true });

        // 2. Update the MAIN APK config (used by the app for updates)
        // This fixes the "Download URL not available" issue
        const mainConfigRef = doc(db, 'config/apkVersion');
        await setDoc(mainConfigRef, {
            version: apkInfo.version,
            downloadUrl: apkInfo.downloadUrl,
            fileSize: apkInfo.sizeFormatted,
            releaseDate: new Date().toISOString(),
            // Don't overwrite other fields like changelog or forceUpdate if they exist
            // But ensure they exist if document is new
        }, { merge: true });

        console.log('✅ APK config saved to Firestore (both upload & main config)');
    } catch (error) {
        console.error('❌ Failed to save APK config:', error);
        throw error;
    }
}

/**
 * Get latest APK info from Firestore
 */
export async function getLatestAPKInfo(): Promise<APKFileInfo | null> {
    try {
        const configRef = doc(db, APK_CONFIG_DOC);
        const configSnap = await getDoc(configRef);

        if (!configSnap.exists()) {
            console.log('ℹ️ No APK uploaded yet');
            return null;
        }

        const data = configSnap.data();
        return data.latest as APKFileInfo;
    } catch (error) {
        console.error('❌ Failed to get APK info:', error);
        return null;
    }
}

/**
 * Download latest APK directly from Firebase Storage
 */
export async function downloadLatestAPKFromFirebase(): Promise<APKFileInfo | null> {
    try {
        const apkInfo = await getLatestAPKInfo();

        if (!apkInfo) {
            throw new Error('No APK file available. Please upload an APK in the Admin Dashboard.');
        }

        // Trigger download
        const link = document.createElement('a');
        link.href = apkInfo.downloadUrl;
        link.download = apkInfo.filename;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        console.log('✅ APK download initiated:', apkInfo.filename);

        return apkInfo;
    } catch (error) {
        console.error('❌ Download error:', error);
        throw error;
    }
}

/**
 * Delete old APK files from storage (keep only latest)
 */
export async function deleteOldAPKFiles(keepFilename: string): Promise<void> {
    try {
        const storageRef = ref(storage, APK_STORAGE_PATH);
        const fileList = await listAll(storageRef);

        const deletePromises = fileList.items
            .filter(item => item.name !== keepFilename)
            .map(item => deleteObject(item));

        await Promise.all(deletePromises);

        console.log(`✅ Deleted ${deletePromises.length} old APK file(s)`);
    } catch (error) {
        console.error('❌ Failed to delete old APK files:', error);
        // Don't throw - this is cleanup, not critical
    }
}

/**
 * Get all APK files in storage
 */
export async function listAllAPKFiles(): Promise<{ name: string; size: number; updated: Date }[]> {
    try {
        const storageRef = ref(storage, APK_STORAGE_PATH);
        const fileList = await listAll(storageRef);

        const filePromises = fileList.items.map(async (item) => {
            const metadata = await getMetadata(item);
            return {
                name: item.name,
                size: metadata.size,
                updated: new Date(metadata.updated)
            };
        });

        const files = await Promise.all(filePromises);

        // Sort by update time, newest first
        return files.sort((a, b) => b.updated.getTime() - a.updated.getTime());
    } catch (error) {
        console.error('❌ Failed to list APK files:', error);
        return [];
    }
}

/**
 * Format bytes to human-readable size
 */
function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}
